import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'ld_chat';
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; } };

export function useVisitorChat(open) {
  const [session, setSession] = useState(load);
  const [messages, setMessages] = useState([]);
  const [meta, setMeta] = useState({ admin_online: false, has_contact: false });
  const [unread, setUnread] = useState(0);
  const lastAt = useRef('');
  const typingTimer = useRef(null);
  const typingLast = useRef(0);

  // advance=false for messages returned by send/upload so newer-than-last-poll replies are not skipped
  const merge = (list, advance = true) => {
    if (!list.length) return 0;
    let added = [];
    setMessages((prev) => {
      const ids = new Set(prev.map((m) => m.id));
      added = list.filter((m) => !ids.has(m.id));
      return added.length ? [...prev, ...added] : prev;
    });
    if (advance) lastAt.current = list[list.length - 1].at;
    return list.filter((m) => m.sender !== 'visitor').length;
  };

  const poll = useCallback(async () => {
    if (!session) return;
    const hadHistory = !!lastAt.current;
    const r = await fetch(`${API}/api/chat/${session.chat_id}/poll?token=${encodeURIComponent(session.token)}&after=${encodeURIComponent(lastAt.current)}`);
    if (r.status === 404) { localStorage.removeItem(KEY); setSession(null); setMessages([]); lastAt.current = ''; return; }
    if (!r.ok) return;
    const d = await r.json();
    const incoming = merge(d.messages);
    setMeta({ admin_online: d.admin_online, has_contact: d.has_contact });
    if (!open && hadHistory && incoming) setUnread((u) => u + incoming);
  }, [session, open]);

  useEffect(() => {
    if (!session) return undefined;
    poll().catch(() => {});
    const t = setInterval(() => poll().catch(() => {}), open ? 2500 : 12000);
    return () => clearInterval(t);
  }, [session, open, poll]);

  useEffect(() => { if (open) setUnread(0); }, [open]);

  const send = async (text, details = {}) => {
    const r = await fetch(`${API}/api/chat/send`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...details, text, chat_id: session?.chat_id, token: session?.token, page: window.location.pathname, session_id: sessionStorage.getItem('ld_session_id') || '' }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(typeof e.detail === 'string' ? e.detail : 'Message not sent — please try again.'); }
    const d = await r.json();
    if (!session) { const s = { chat_id: d.chat_id, token: d.token }; localStorage.setItem(KEY, JSON.stringify(s)); setSession(s); }
    merge(d.messages, false);
  };

  const saveDetails = async (details) => {
    const r = await fetch(`${API}/api/chat/${session.chat_id}/details`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...details, token: session.token }),
    });
    if (!r.ok) throw new Error('Please add an email or phone number.');
    setMeta((m) => ({ ...m, has_contact: true }));
    await poll();
  };

  const fileHref = (f) => `${API}/api/chat/${session?.chat_id}/files/${f.id}?token=${encodeURIComponent(session?.token || '')}`;
  const uploadUrl = session ? `${API}/api/chat/${session.chat_id}/upload` : '';
  const addMessage = (m) => merge([m], false);

  const emailTranscript = async () => {
    const r = await fetch(`${API}/api/chat/${session.chat_id}/transcript`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: session.token }) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(typeof e.detail === 'string' ? e.detail : 'Could not send the email.'); }
    await poll();
  };

  // Live typing preview for the team: at most one update every 700 ms, plus a trailing update
  const typing = (value) => {
    if (!session) return;
    const push = () => {
      typingLast.current = Date.now();
      fetch(`${API}/api/chat/${session.chat_id}/typing`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: session.token, text: value.slice(0, 2000) }) }).catch(() => {});
    };
    clearTimeout(typingTimer.current);
    if (Date.now() - typingLast.current > 700) push(); else typingTimer.current = setTimeout(push, 700);
  };

  return { messages, meta, unread, send, saveDetails, started: !!session, session, fileHref, uploadUrl, addMessage, emailTranscript, typing };
}
